import React, { useEffect, useState } from 'react';
import { Download, X, Smartphone, Share, Monitor, ChevronRight } from 'lucide-react';

/**
 * InstallPwaButton — floating "Download Now" CTA.
 *
 * Props:
 *   withBottomNav — when mounted inside AppShell (which renders a persistent
 *                   bottom navigation), bump the pill above it so it doesn't
 *                   overlap the nav icons on desktop.
 *
 * Behaviour:
 *   • Always renders on supported pages after a short delay (gives the page
 *     time to settle so the button doesn't fight with the splash overlay).
 *   • Captures `beforeinstallprompt` when fired (Android Chrome/Edge/Samsung) —
 *     clicking the button triggers the native install dialog.
 *   • If no native prompt is available (iOS Safari, Firefox, desktop Safari,
 *     Chrome without engagement heuristics met) — clicking opens a small sheet
 *     with browser-specific "Add to Home Screen" / install instructions.
 *   • Auto-hides when the app is already installed (display-mode: standalone).
 *   • Dismissible — won't re-show for 7 days (localStorage flag).
 */
const SUPPRESS_KEY = 'worldvibe.pwa.dismissedAt';
const SUPPRESS_DAYS = 7;
const APPEAR_DELAY_MS = 1500;

// Native store URLs — on mobile the button routes travelers straight to the
// right store instead of the PWA install prompt.
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.worldvibe.app';
const APP_STORE_URL = 'https://apps.apple.com/app/id6795453750';

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator?.standalone === true
  );
}

function detectPlatform() {
  const ua = (window.navigator.userAgent || '').toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua) && !/crios|fxios|edgios/.test(ua);
  const isAndroid = /android/.test(ua);
  const isMac = /macintosh|mac os x/.test(ua) && !isIOS;
  const isFirefox = /firefox|fxios/.test(ua);
  const isEdge = /edg\//.test(ua);
  const isChrome = /chrome|crios/.test(ua) && !isEdge && !/opr|opera/.test(ua);
  const isSafari = /safari/.test(ua) && !isChrome && !isEdge && !isFirefox && !isAndroid;
  return { isIOS, isAndroid, isMac, isFirefox, isEdge, isChrome, isSafari };
}

function recentlyDismissed() {
  const ts = Number(localStorage.getItem(SUPPRESS_KEY) || 0);
  if (!ts) return false;
  return Date.now() - ts < SUPPRESS_DAYS * 24 * 60 * 60 * 1000;
}

export default function InstallPwaButton({ withBottomNav = false }) {
  const [promptEvent, setPromptEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const platform = detectPlatform();

  useEffect(() => {
    if (isStandalone()) return undefined;
    if (recentlyDismissed()) return undefined;

    // Reveal the button shortly after mount so it doesn't fight with the
    // splash overlay and so the page has a moment to settle.
    const t = setTimeout(() => setVisible(true), APPEAR_DELAY_MS);

    const onBeforeInstall = (e) => {
      // Hold onto the event so we can call .prompt() later.
      e.preventDefault();
      setPromptEvent(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setPromptEvent(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      clearTimeout(t);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(SUPPRESS_KEY, String(Date.now()));
    setVisible(false);
    setSheetOpen(false);
  };

  const install = async () => {
    // Mobile devices: route straight to the appropriate app store instead of
    // triggering the PWA install prompt — travelers expect a real app, not a
    // browser bookmark.
    if (platform.isIOS) {
      window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer');
      return;
    }
    if (platform.isAndroid) {
      window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
      return;
    }
    // Desktop: keep the PWA install flow.
    // If Chrome/Edge captured the native event, trigger it.
    if (promptEvent) {
      try {
        promptEvent.prompt();
        const choice = await promptEvent.userChoice;
        if (choice?.outcome === 'accepted') {
          setVisible(false);
        } else {
          // User cancelled — remember for 7 days so we don't nag.
          localStorage.setItem(SUPPRESS_KEY, String(Date.now()));
          setVisible(false);
        }
        setPromptEvent(null);
        return;
      } catch {
        // fall through to instructions sheet
      }
    }
    // No native prompt available — show instructions sheet.
    setSheetOpen(true);
  };

  return (
    <>
      <div
        data-testid="pwa-install-floating"
        className={`fixed z-[60] right-4 sm:right-6 flex items-center gap-2 bottom-20 ${withBottomNav ? 'sm:bottom-24' : 'sm:bottom-6'}`}
        style={{ animation: 'fadeInUp 0.5s ease-out' }}
      >
        <button
          type="button"
          onClick={install}
          data-testid="pwa-install-btn"
          className="group flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-fuchsia-500 via-purple-600 to-cyan-500 text-white font-semibold shadow-lg shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 hover:scale-105 active:scale-95 transition-all border border-white/15 backdrop-blur"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/80 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
          </span>
          <Download size={16} className="group-hover:translate-y-0.5 transition" />
          <span className="text-sm">Download App</span>
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          aria-label="Dismiss install prompt"
          className="w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 border border-white/15 backdrop-blur flex items-center justify-center text-white/80"
        >
          <X size={15} />
        </button>
      </div>

      {/* Instructions sheet — opened when no native prompt is available */}
      {sheetOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setSheetOpen(false)}
          data-testid="pwa-install-sheet"
        >
          <div
            className="w-full sm:max-w-md mx-auto bg-[#161622] border border-white/10 rounded-t-2xl sm:rounded-2xl p-5 text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              {platform.isIOS || platform.isAndroid ? (
                <Smartphone className="text-cyan-300" size={20} />
              ) : (
                <Monitor className="text-cyan-300" size={20} />
              )}
              <div className="font-display font-semibold text-lg">Install WorldVibe</div>
            </div>

            <InstallSteps platform={platform} />

            <button
              type="button"
              onClick={() => setSheetOpen(false)}
              data-testid="pwa-install-sheet-close"
              className="mt-5 w-full py-2.5 rounded-full bg-white/10 hover:bg-white/15 font-medium text-sm"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="w-6 h-6 shrink-0 rounded-full bg-fuchsia-500/20 text-fuchsia-200 flex items-center justify-center text-xs font-bold">
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}

function InstallSteps({ platform }) {
  if (platform.isIOS) {
    return (
      <>
        <p className="text-sm text-white/70 mb-4">On iPhone/iPad install in two taps:</p>
        <ol className="space-y-3 text-sm">
          <Step n={1}>
            Tap the <Share size={14} className="inline -mt-0.5 mx-0.5 text-cyan-300" />{' '}
            <span className="font-medium">Share</span> button in Safari.
          </Step>
          <Step n={2}>
            Choose <span className="font-medium">&ldquo;Add to Home Screen&rdquo;</span> — done!
          </Step>
        </ol>
      </>
    );
  }
  if (platform.isAndroid) {
    return (
      <>
        <p className="text-sm text-white/70 mb-4">On Android Chrome:</p>
        <ol className="space-y-3 text-sm">
          <Step n={1}>Tap the menu (three dots) in the top-right corner.</Step>
          <Step n={2}>
            Choose <span className="font-medium">&ldquo;Add to Home Screen&rdquo;</span> or{' '}
            <span className="font-medium">&ldquo;Install app&rdquo;</span>.
          </Step>
        </ol>
      </>
    );
  }
  if (platform.isFirefox) {
    return (
      <>
        <p className="text-sm text-white/70 mb-4">Firefox doesn&apos;t support installing web apps directly. Try one of:</p>
        <ul className="space-y-2 text-sm">
          <li className="flex items-start gap-2">
            <ChevronRight size={14} className="mt-0.5 text-cyan-300 shrink-0" />
            <span>Bookmark this page (<kbd className="px-1.5 py-0.5 rounded bg-white/10 text-xs">Ctrl/⌘ + D</kbd>) for quick access.</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight size={14} className="mt-0.5 text-cyan-300 shrink-0" />
            <span>Or open WorldVibe in <span className="font-medium">Chrome / Edge</span> and tap the install icon.</span>
          </li>
        </ul>
      </>
    );
  }
  if (platform.isSafari) {
    return (
      <>
        <p className="text-sm text-white/70 mb-4">On macOS Safari:</p>
        <ol className="space-y-3 text-sm">
          <Step n={1}>
            Open the <span className="font-medium">File</span> menu in Safari.
          </Step>
          <Step n={2}>
            Click <span className="font-medium">&ldquo;Add to Dock&hellip;&rdquo;</span> (Safari 17+) to install WorldVibe as a Mac app.
          </Step>
        </ol>
      </>
    );
  }
  // Default: Chrome/Edge desktop (the native prompt should have fired, but
  // some Chromium builds suppress it).
  return (
    <>
      <p className="text-sm text-white/70 mb-4">Install WorldVibe as an app on your desktop:</p>
      <ol className="space-y-3 text-sm">
        <Step n={1}>
          Look for the install icon{' '}
          <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-white/10 align-middle">
            <Download size={12} />
          </span>{' '}
          at the right end of the address bar.
        </Step>
        <Step n={2}>
          Or open the browser menu (three dots) →{' '}
          <span className="font-medium">&ldquo;Install WorldVibe&rdquo;</span>.
        </Step>
      </ol>
    </>
  );
}
